import * as THREE from "three";
import { worldTextureUrl } from "../adventure/ActiveAdventure.js";

/**
 * PBR material factory (W20).
 *
 * Returns `MeshStandardMaterial` instances tinted by a fallback color and
 * — when the active adventure ships them — overlaid with authored albedo,
 * normal, and roughness maps.
 *
 * Texture URLs follow the per-adventure convention:
 *   `<adventureBase>/world/textures/<surface>/{albedo,normal,roughness}.png`
 *
 * Maps that 404 are silently dropped so an adventure can opt into any
 * subset (e.g. ship a normal map but no albedo). The base color always
 * remains visible underneath.
 */

export interface SurfaceMaterialOptions {
  /** Subdirectory under `world/textures/` (e.g. "floor", "wall"). */
  readonly surface: string;
  /** Fallback diffuse color used until/unless an albedo map loads. */
  readonly color: number;
  readonly side?: THREE.Side;
  readonly roughness?: number;
  readonly metalness?: number;
  /**
   * Named texture set key (T02). When set, albedo/normal/roughness resolve
   * to `world/textures/sets/<setKey>/<surface>/…` first; a 404 falls
   * through to the adventure default `world/textures/<surface>/…`.
   */
  readonly setKey?: string;
}

type MapKind = "albedo" | "normal" | "roughness";

export interface MaterialFactoryDeps {
  /** Loads a texture URL. Resolves with `null` if the asset is missing. */
  readonly loadTexture: (url: string) => Promise<THREE.Texture | null>;
}

const defaultLoader = new THREE.TextureLoader();

const defaultDeps: MaterialFactoryDeps = {
  loadTexture: (url) =>
    new Promise((resolve) => {
      defaultLoader.load(
        url,
        (tex) => resolve(tex),
        undefined,
        () => resolve(null),
      );
    }),
};

/**
 * Build a `MeshStandardMaterial` and asynchronously attach optional
 * authored maps. Returns synchronously so callers can wire it into a
 * mesh immediately; maps land later when fetches resolve.
 */
export function createSurfaceMaterial(
  options: SurfaceMaterialOptions,
  deps: MaterialFactoryDeps = defaultDeps,
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: options.color,
    side: options.side ?? THREE.FrontSide,
    roughness: options.roughness ?? 0.85,
    metalness: options.metalness ?? 0.0,
  });

  void attachMaps(material, options.surface, options.setKey, deps);
  return material;
}

async function attachMaps(
  material: THREE.MeshStandardMaterial,
  surface: string,
  setKey: string | undefined,
  deps: MaterialFactoryDeps,
): Promise<void> {
  const kinds: MapKind[] = ["albedo", "normal", "roughness"];
  const loaded = await Promise.all(
    kinds.map(async (kind) => {
      // T02: if a set is named, try the set-specific URL first;
      // on miss, fall through to the adventure default. This lets
      // a set declare only the surfaces it wants to override.
      if (setKey) {
        const setUrl = worldTextureUrl(surface, kind, setKey);
        const setTex = await deps.loadTexture(setUrl);
        if (setTex) return [kind, setTex] as const;
      }
      const url = worldTextureUrl(surface, kind);
      const tex = await deps.loadTexture(url);
      return [kind, tex] as const;
    }),
  );

  let touched = false;
  for (const [kind, tex] of loaded) {
    if (!tex) continue;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    if (kind === "albedo") {
      tex.colorSpace = THREE.SRGBColorSpace;
      material.map = tex;
    } else if (kind === "normal") {
      material.normalMap = tex;
    } else if (kind === "roughness") {
      material.roughnessMap = tex;
    }
    touched = true;
  }
  if (touched) {
    material.needsUpdate = true;
  }
}
